import { useState, useCallback } from "react";
import { Button, Typography } from "@volvo/vcdk-react";
import styles from "./SupportForm.module.css";
import {
    submitSupportRequest,
    type SupportRequestResponse,
} from "../../../api/apiService";

interface SupportFormProps {
    brand: string;
    onClose: () => void;
}

const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const MAX_TOTAL_SIZE = 15 * 1024 * 1024; // 15MB across all attachments
const MAX_ATTACHMENTS = 5;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirror the server-side limits (SupportRequestViewModel) so users get a clear,
// field-specific message instead of a generic 400 from the API.
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_SUBJECT_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 2000;

// multipart/form-data normalises line breaks to CRLF, so each newline counts as
// two characters on the server. Measure length the same way the backend will.
const serverLength = (value: string): number =>
    value.replace(/\r\n|\r|\n/g, "\r\n").length;

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isSameFile = (a: File, b: File): boolean =>
    a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

type SupportFieldErrors = {
    requesterName?: string;
    requesterEmail?: string;
    subject?: string;
    message?: string;
};

const SupportForm = ({ brand, onClose }: SupportFormProps) => {
    const [requesterName, setRequesterName] = useState("");
    const [requesterEmail, setRequesterEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState<SupportFieldErrors>({});
    const [result, setResult] = useState<SupportRequestResponse | null>(null);

    const handleFilesSelected = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = Array.from(e.target.files || []);
            // Reset the input so the same file (or a file from another location)
            // can be selected again on a subsequent pick.
            e.target.value = "";
            if (selected.length === 0) {
                return;
            }

            const next = [...attachments];
            let runningTotal = next.reduce((sum, f) => sum + f.size, 0);
            let validationError = "";

            for (const file of selected) {
                if (next.some((existing) => isSameFile(existing, file))) {
                    continue;
                }
                if (next.length >= MAX_ATTACHMENTS) {
                    validationError = `Maximum ${MAX_ATTACHMENTS} attachments allowed.`;
                    break;
                }
                const ext = "." + file.name.split(".").pop()?.toLowerCase();
                if (!ALLOWED_EXTENSIONS.includes(ext)) {
                    validationError = `File "${file.name}" is not allowed. Only JPG, JPEG, PNG, and PDF files are accepted.`;
                    continue;
                }
                if (file.size > MAX_FILE_SIZE) {
                    validationError = `File "${file.name}" exceeds the 10MB per-file size limit.`;
                    continue;
                }
                if (runningTotal + file.size > MAX_TOTAL_SIZE) {
                    validationError = `Total attachment size exceeds the 15MB limit.`;
                    continue;
                }
                next.push(file);
                runningTotal += file.size;
            }

            setAttachments(next);
            setError(validationError);
        },
        [attachments]
    );

    const handleRemoveAttachment = useCallback((index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
        setError("");
    }, []);

    const validateFields = useCallback((): SupportFieldErrors => {
        const errors: SupportFieldErrors = {};
        const name = requesterName.trim();
        const email = requesterEmail.trim();
        const subjectValue = subject.trim();
        const messageValue = message.trim();

        if (!name) {
            errors.requesterName = "Please enter your name.";
        } else if (name.length > MAX_NAME_LENGTH) {
            errors.requesterName = `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
        }

        if (!email) {
            errors.requesterEmail = "Please enter your email address.";
        } else if (!EMAIL_REGEX.test(email)) {
            errors.requesterEmail = "Please enter a valid email address.";
        } else if (email.length > MAX_EMAIL_LENGTH) {
            errors.requesterEmail = `Email must be ${MAX_EMAIL_LENGTH} characters or fewer.`;
        }

        if (!subjectValue) {
            errors.subject = "Please enter a subject.";
        } else if (subjectValue.length > MAX_SUBJECT_LENGTH) {
            errors.subject = `Subject must be ${MAX_SUBJECT_LENGTH} characters or fewer.`;
        }

        if (!messageValue) {
            errors.message = "Please enter a message.";
        } else if (serverLength(messageValue) > MAX_MESSAGE_LENGTH) {
            errors.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or fewer (line breaks count as two).`;
        }

        return errors;
    }, [requesterName, requesterEmail, subject, message]);

    const clearFieldError = useCallback((field: keyof SupportFieldErrors) => {
        setFieldErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const handleFieldBlur = useCallback(
        (field: keyof SupportFieldErrors) => {
            const errors = validateFields();
            setFieldErrors((prev) => {
                const next = { ...prev };
                if (errors[field]) {
                    next[field] = errors[field];
                } else {
                    delete next[field];
                }
                return next;
            });
        },
        [validateFields]
    );

    const handleSubmit = useCallback(async () => {
        setError("");

        const errors = validateFields();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await submitSupportRequest(
                {
                    brand,
                    requesterName: requesterName.trim(),
                    requesterEmail: requesterEmail.trim(),
                    subject: subject.trim(),
                    message: message.trim(),
                    currentPageUrl: window.location.href,
                },
                attachments.length > 0 ? attachments : undefined
            );
            setResult(response);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "An unexpected error occurred."
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        requesterName,
        requesterEmail,
        subject,
        message,
        brand,
        attachments,
        validateFields,
    ]);

    if (result) {
        return (
            <div className={styles.successContainer}>
                <Typography variant="subtitle1">
                    Support request submitted
                </Typography>
                <p>Your request has been accepted for processing.</p>
                <p>Your reference number:</p>
                <div className={styles.referenceId}>
                    {result.supportReferenceId}
                </div>
                <p>
                    Please keep this reference number for future correspondence.
                </p>
                <div className={styles.buttonRow}>
                    <Button variant="primary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.supportFormContainer}>
            <div className={styles.formField}>
                <label htmlFor="supportName">Your Name *</label>
                <input
                    id="supportName"
                    type="text"
                    value={requesterName}
                    onChange={(e) => {
                        setRequesterName(e.target.value);
                        clearFieldError("requesterName");
                    }}
                    onBlur={() => handleFieldBlur("requesterName")}
                    className={
                        fieldErrors.requesterName ? styles.inputError : undefined
                    }
                    aria-invalid={Boolean(fieldErrors.requesterName)}
                    maxLength={MAX_NAME_LENGTH}
                />
                <div className={styles.errorText} aria-live="polite">
                    {fieldErrors.requesterName}
                </div>
            </div>

            <div className={styles.formField}>
                <label htmlFor="supportEmail">Your Email *</label>
                <input
                    id="supportEmail"
                    type="email"
                    value={requesterEmail}
                    onChange={(e) => {
                        setRequesterEmail(e.target.value);
                        clearFieldError("requesterEmail");
                    }}
                    onBlur={() => handleFieldBlur("requesterEmail")}
                    className={
                        fieldErrors.requesterEmail ? styles.inputError : undefined
                    }
                    aria-invalid={Boolean(fieldErrors.requesterEmail)}
                    maxLength={MAX_EMAIL_LENGTH}
                />
                <div className={styles.errorText} aria-live="polite">
                    {fieldErrors.requesterEmail}
                </div>
            </div>

            <div className={styles.formField}>
                <label htmlFor="supportSubject">Subject *</label>
                <input
                    id="supportSubject"
                    type="text"
                    value={subject}
                    onChange={(e) => {
                        setSubject(e.target.value);
                        clearFieldError("subject");
                    }}
                    onBlur={() => handleFieldBlur("subject")}
                    className={
                        fieldErrors.subject ? styles.inputError : undefined
                    }
                    aria-invalid={Boolean(fieldErrors.subject)}
                    maxLength={MAX_SUBJECT_LENGTH}
                />
                <div className={styles.errorText} aria-live="polite">
                    {fieldErrors.subject}
                </div>
            </div>

            <div className={styles.formField}>
                <label htmlFor="supportMessage">Message *</label>
                <textarea
                    id="supportMessage"
                    value={message}
                    onChange={(e) => {
                        setMessage(e.target.value);
                        clearFieldError("message");
                    }}
                    onBlur={() => handleFieldBlur("message")}
                    className={
                        fieldErrors.message ? styles.inputError : undefined
                    }
                    aria-invalid={Boolean(fieldErrors.message)}
                    maxLength={MAX_MESSAGE_LENGTH}
                />
                <div className={styles.errorText} aria-live="polite">
                    {fieldErrors.message}
                </div>
            </div>

            <div className={styles.formField}>
                <label htmlFor="supportAttachments">
                    Attachments (optional)
                </label>
                <input
                    id="supportAttachments"
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFilesSelected}
                    disabled={
                        isSubmitting || attachments.length >= MAX_ATTACHMENTS
                    }
                />
                <div className={styles.attachmentInfo}>
                    JPG, JPEG, PNG, PDF files up to 10MB each, 15MB total.
                    Maximum {MAX_ATTACHMENTS} files. You can add them one at a
                    time or several at once ({attachments.length}/
                    {MAX_ATTACHMENTS} added).
                </div>

                {attachments.length > 0 && (
                    <ul className={styles.attachmentList}>
                        {attachments.map((file, index) => (
                            <li
                                key={`${file.name}-${file.lastModified}-${file.size}`}
                                className={styles.attachmentItem}
                            >
                                <span
                                    className={styles.attachmentName}
                                    title={file.name}
                                >
                                    {file.name}
                                </span>
                                <span className={styles.attachmentSize}>
                                    {formatFileSize(file.size)}
                                </span>
                                <button
                                    type="button"
                                    className={styles.removeAttachment}
                                    onClick={() =>
                                        handleRemoveAttachment(index)
                                    }
                                    aria-label={`Remove ${file.name}`}
                                    disabled={isSubmitting}
                                >
                                    &times;
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {error && <div className={styles.errorText}>{error}</div>}

            <div className={styles.buttonRow}>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
};

export default SupportForm;
